import { useEffect, useMemo, useState } from "react";
import { useCreateGasStation, useGasStationBrands } from "@/lib/queries";
import { imageSrc } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { Field, Input, Select } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";

function defaultForm(defaultBrandID = "") {
  return {
    brandID: defaultBrandID,
    name: "",
    address: "",
    parish: "",
    fuelType: "",
    slug: "",
    fuelPrice_87: "",
    fuelPrice_90: "",
    fuelPrice_Diesel: "",
    fuelPrice_ULSD: "",
    coordinatesLat: "",
    coordinatesLong: "",
  };
}

function parseNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function AddGasStationModal({
  open,
  onClose,
  defaultBrandID = "",
}: {
  open: boolean;
  onClose: () => void;
  defaultBrandID?: string;
}) {
  const createStation = useCreateGasStation();
  const { data: brands = [], isLoading: brandsLoading } = useGasStationBrands();
  const [form, setForm] = useState(() => defaultForm(defaultBrandID));
  const [error, setError] = useState<string | null>(null);
  const saving = createStation.isPending;

  const selectedBrand = useMemo(
    () => brands.find((brand) => brand.id === form.brandID) ?? null,
    [brands, form.brandID]
  );
  const brandLogoSrc = imageSrc(selectedBrand?.brandLogoUrl);

  const brandOptions = useMemo(
    () =>
      brands
        .filter((brand) => brand.id)
        .map((brand) => ({
          value: brand.id,
          label: brand.brandName ?? "Unnamed brand",
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [brands]
  );

  useEffect(() => {
    if (!open) return;
    setForm(defaultForm(defaultBrandID));
    setError(null);
  }, [open, defaultBrandID]);

  function closeModal() {
    if (saving) return;
    onClose();
  }

  async function handleSave() {
    const name = form.name.trim();
    const address = form.address.trim();
    const brandID = form.brandID.trim();

    if (!brandID) {
      setError("Select a station brand.");
      return;
    }
    if (!name) {
      setError("Station name is required.");
      return;
    }
    if (!address) {
      setError("Address is required.");
      return;
    }

    setError(null);
    try {
      await createStation.mutateAsync({
        name,
        address,
        brandID,
        parish: form.parish.trim() || null,
        fuelType: form.fuelType.trim() || null,
        slug: form.slug.trim() || null,
        fuelPrice_87: parseNumber(form.fuelPrice_87),
        fuelPrice_90: parseNumber(form.fuelPrice_90),
        fuelPrice_Diesel: parseNumber(form.fuelPrice_Diesel),
        fuelPrice_ULSD: parseNumber(form.fuelPrice_ULSD),
        coordinatesLat: parseNumber(form.coordinatesLat),
        coordinatesLong: parseNumber(form.coordinatesLong),
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add station");
    }
  }

  const canSave = Boolean(form.brandID && form.name.trim() && form.address.trim());

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title="Add gas station"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="brand" onClick={handleSave} disabled={saving || !canSave}>
            {saving ? "Adding…" : "Add station"}
          </Button>
        </>
      }
    >
      {error ? <p className="mb-4 rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p> : null}
      <div className="space-y-4 text-foreground">
        <Field label="Brand">
          <Select
            value={form.brandID}
            onChange={(e) => setForm((current) => ({ ...current, brandID: e.target.value }))}
            disabled={brandsLoading}
          >
            <option value="">Select a brand</option>
            {brandOptions.map((brand) => (
              <option key={brand.value} value={brand.value}>
                {brand.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Station name">
          <Input
            value={form.name}
            onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
            placeholder="e.g. Rubis Half Way Tree"
            autoFocus
          />
        </Field>

        <Field label="Address">
          <Input
            value={form.address}
            onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Parish">
            <Input
              value={form.parish}
              onChange={(e) => setForm((current) => ({ ...current, parish: e.target.value }))}
            />
          </Field>
          <Field label="Fuel type">
            <Input
              value={form.fuelType}
              onChange={(e) => setForm((current) => ({ ...current, fuelType: e.target.value }))}
              placeholder="e.g. Gasoline"
            />
          </Field>
        </div>

        {form.brandID ? (
          <Field label="Station logo">
            <div className="flex h-24 items-center justify-center rounded-2xl bg-canvas p-3">
              {brandLogoSrc ? (
                <img src={brandLogoSrc} alt="" className="max-h-full max-w-full object-contain" />
              ) : (
                <p className="text-sm text-muted">This brand has no logo yet.</p>
              )}
            </div>
            <p className="mt-2 text-xs text-muted">
              The selected brand logo is copied to the new station automatically. Edit the station later to use a custom logo.
            </p>
          </Field>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="E-10 87">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_87}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_87: e.target.value }))}
            />
          </Field>
          <Field label="E-10 90">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_90}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_90: e.target.value }))}
            />
          </Field>
          <Field label="Diesel">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_Diesel}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_Diesel: e.target.value }))}
            />
          </Field>
          <Field label="ULSD">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_ULSD}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_ULSD: e.target.value }))}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <Input
              type="number"
              step="any"
              value={form.coordinatesLat}
              onChange={(e) => setForm((current) => ({ ...current, coordinatesLat: e.target.value }))}
            />
          </Field>
          <Field label="Longitude">
            <Input
              type="number"
              step="any"
              value={form.coordinatesLong}
              onChange={(e) => setForm((current) => ({ ...current, coordinatesLong: e.target.value }))}
            />
          </Field>
        </div>

        <Field label="Slug (optional)">
          <Input
            value={form.slug}
            onChange={(e) => setForm((current) => ({ ...current, slug: e.target.value }))}
          />
        </Field>

        <p className="text-xs text-muted">
          A unique station ID is generated automatically for the iOS app. The selected brand is saved to brandID.
        </p>
      </div>
    </Modal>
  );
}
